// Rung 2 — doc & URL blips.
//
// The point of this page is that the work is visible. The endpoint streams a
// stage event per step — and the fetching/extracting events carry the host it
// reached and the characters it recovered — so the timeline below is driven by
// the backend's real progress, never by a fake timer.

import { useRef, useState } from 'react';
import { streamBlip, type BlipResult, type StageEvent } from '../lib/blip';
import { StageTimeline, type StageEntry } from '../components/blip/StageTimeline';
import { BriefView } from '../components/blip/BriefView';

const MAX_CHARS = 60000;

type Mode = 'url' | 'text';
type Status = 'idle' | 'running' | 'done' | 'error';

export default function Blip() {
  const [mode, setMode] = useState<Mode>('url');
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [entries, setEntries] = useState<StageEntry[]>([]);
  const [result, setResult] = useState<BlipResult | null>(null);
  const [resultSource, setResultSource] = useState<string | null>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const tooLong = text.length > MAX_CHARS;
  const running = status === 'running';
  const canSubmit = !running && !tooLong && (mode === 'url' ? url.trim() !== '' : text.trim() !== '');

  const closeOpenStage = () =>
    setEntries((prev) => prev.map((e) => (e.endedAt === null ? { ...e, endedAt: Date.now() } : e)));

  const onStage = (event: StageEvent) => {
    const now = Date.now();
    setEntries((prev) => {
      const closed = prev.map((e) => (e.endedAt === null ? { ...e, endedAt: now } : e));
      // The backend may repeat a stage (a retry); the first entry keeps its start
      // time so the rail never jumps backwards, but the newer detail wins.
      const seen = closed.findIndex((e) => e.stage === event.stage);
      if (seen !== -1) {
        closed[seen] = { ...closed[seen], detail: { ...closed[seen].detail, ...event } };
        return closed;
      }
      return [...closed, { stage: event.stage, startedAt: now, endedAt: null, detail: event }];
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const controller = new AbortController();
    abortRef.current = controller;
    const submittedUrl = mode === 'url' ? url.trim() : null;

    setStatus('running');
    setEntries([]);
    setResult(null);
    setResultSource(null);
    setError('');

    try {
      const payload = submittedUrl ? { url: submittedUrl } : { text: text.trim() };
      const res = await streamBlip(payload, { onStage, signal: controller.signal });
      closeOpenStage();
      setResult(res);
      setResultSource(submittedUrl);
      setStatus('done');
    } catch (err) {
      closeOpenStage();
      if (err instanceof DOMException && err.name === 'AbortError') {
        setStatus('idle');
        setEntries([]);
        return;
      }
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    } finally {
      abortRef.current = null;
    }
  };

  const cancel = () => abortRef.current?.abort();

  const reset = () => {
    setStatus('idle');
    setEntries([]);
    setResult(null);
    setResultSource(null);
    setError('');
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10">
      <header className="mb-8 border-b border-edge pb-7">
        <h1 className="wordmark font-mono text-4xl font-bold tracking-tight sm:text-5xl">
          Blip a doc
        </h1>
        <p className="mt-3 max-w-xl text-base font-light leading-relaxed text-muted">
          Point it at a page, or paste the text. You will see it{' '}
          <b className="font-semibold text-ink">validate</b>,{' '}
          <b className="font-semibold text-ink">fetch</b>,{' '}
          <b className="font-semibold text-ink">extract</b> and{' '}
          <b className="font-semibold text-ink">compose</b> — and every quote is checked
          against the source before it reaches you.
        </p>
      </header>

      <form onSubmit={onSubmit}>
        <div className="mb-6 flex justify-center">
          <div className="inline-flex rounded-lg border border-edge p-1">
            {(['url', 'text'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                disabled={running}
                className={`rounded-md px-4 py-1.5 font-mono text-xs uppercase tracking-wider transition disabled:opacity-40 ${
                  mode === m ? 'bg-teal/15 text-teal' : 'text-muted hover:text-ink'
                }`}
              >
                {m === 'url' ? 'From a URL' : 'Paste text'}
              </button>
            ))}
          </div>
        </div>

        {mode === 'url' ? (
          <div>
            <label htmlFor="blip-url" className="sr-only">
              Page address
            </label>
            <input
              id="blip-url"
              type="url"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/the-thing-you-have-not-read"
              disabled={running}
              className="w-full rounded-lg border border-edge bg-white/80 px-4 py-3 font-mono text-sm text-ink placeholder:text-muted focus:border-teal focus:outline-none disabled:opacity-60"
            />
            <p className="mt-1.5 font-mono text-xs text-muted">
              Public pages only — the fetcher will not reach private or internal addresses.
            </p>
          </div>
        ) : (
          <div>
            <label htmlFor="blip-text" className="sr-only">
              Text to brief
            </label>
            <textarea
              id="blip-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the document, the email thread, the filing..."
              rows={12}
              disabled={running}
              className="w-full resize-y rounded-lg border border-edge bg-white/80 px-4 py-3 font-mono text-sm leading-relaxed text-ink placeholder:text-muted focus:border-teal focus:outline-none disabled:opacity-60"
            />
            <div className="mt-1 flex justify-between font-mono text-xs text-muted">
              <span>
                {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} chars
              </span>
              {tooLong && (
                <span className="text-clay">Too long - split it into shorter sections.</span>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-gradient-to-r from-teal to-clay px-6 py-3 font-semibold text-darker transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-40"
          >
            {running ? 'Briefing...' : 'Brief it'}
          </button>
          {running && (
            <button
              type="button"
              onClick={cancel}
              className="rounded-lg border border-edge px-5 py-3 text-sm font-semibold text-muted transition hover:border-clay/50 hover:text-clay"
            >
              Cancel
            </button>
          )}
          {status === 'done' && (
            <button
              type="button"
              onClick={reset}
              className="rounded-lg border border-edge px-5 py-3 text-sm font-semibold text-muted transition hover:border-teal/50 hover:text-teal"
            >
              Start over
            </button>
          )}
        </div>
      </form>

      {status !== 'idle' && (
        <div className="mt-8">
          <StageTimeline entries={entries} finished={status === 'done'} failed={status === 'error'} />
        </div>
      )}

      {status === 'error' && (
        <div className="mt-5 rounded-lg border border-clay/40 bg-clay/10 px-4 py-3 text-sm text-[#7a3a1c]">
          {error}
        </div>
      )}

      {result && <BriefView result={result} sourceUrl={resultSource} />}
    </div>
  );
}
